/**
 * طبقة المنصة — التعريفات المشتركة بين الخادم والواجهة.
 * مصدر واحد للحقيقة لما يخص الوحدات والحدود والباقات ودورة حياة الاشتراك.
 */

/* ==========================================================================
   الوحدات (Features)
   ========================================================================== */

export const FEATURE_KEYS = [
  'crm',
  'sales',
  'finance',
  'hr',
  'reports',
  'accounting',
  'projects',
  'automation',
  'meta',
] as const;

export type FeatureKey = (typeof FEATURE_KEYS)[number];

export interface FeatureDef {
  key: FeatureKey;
  label: string;
  description: string;
  /** وحدة غير مكتملة بعد — تُعرض في الباقات لكنها غير قابلة للتفعيل. */
  comingSoon?: boolean;
}

export const FEATURES: readonly FeatureDef[] = [
  {
    key: 'crm',
    label: 'إدارة العملاء',
    description: 'العملاء المحتملون والحسابات والصفقات والأنشطة.',
  },
  {
    key: 'sales',
    label: 'المبيعات',
    description: 'عروض الأسعار والعقود وكتالوج الخدمات وأهداف الفريق.',
  },
  {
    key: 'finance',
    label: 'الحسابات',
    description: 'الفواتير والمدفوعات والذمم والعمولات.',
  },
  {
    key: 'hr',
    label: 'الموارد البشرية',
    description: 'الحضور والإجازات وملفات الموظفين والمرتبات.',
  },
  {
    key: 'reports',
    label: 'التقارير',
    description: 'تحليلات المبيعات والعملاء والمالية والموظفين.',
  },
  {
    key: 'accounting',
    label: 'المحاسبة العامة',
    description: 'القيد المزدوج والقوائم المالية.',
    comingSoon: true,
  },
  {
    key: 'projects',
    label: 'المشاريع والمهام',
    description: 'إدارة التنفيذ وتتبع الوقت.',
    comingSoon: true,
  },
  {
    key: 'automation',
    label: 'الأتمتة',
    description: 'سير العمل وتوزيع العملاء والحملات.',
    comingSoon: true,
  },
  {
    key: 'meta',
    label: 'قنوات ميتا',
    description: 'واتساب وماسنجر وإنستجرام في صندوق موحّد.',
    comingSoon: true,
  },
] as const;

export const FEATURE_MAP: Readonly<Record<FeatureKey, FeatureDef>> =
  Object.fromEntries(FEATURES.map((f) => [f.key, f])) as Record<FeatureKey, FeatureDef>;

/* ==========================================================================
   الحدود (Limits)
   ========================================================================== */

export const LIMIT_KEYS = [
  'users',
  'accounts',
  'leads',
  'storageMb',
  'campaignsPerMonth',
  'whatsappMessages',
] as const;

export type LimitKey = (typeof LIMIT_KEYS)[number];

/** القيمة التي تعني «بلا حد». */
export const UNLIMITED = -1;

export interface LimitDef {
  key: LimitKey;
  label: string;
  unit: string;
}

export const LIMIT_DEFS: readonly LimitDef[] = [
  { key: 'users', label: 'المستخدمون', unit: 'مستخدم' },
  { key: 'accounts', label: 'الحسابات', unit: 'حساب' },
  { key: 'leads', label: 'العملاء المحتملون', unit: 'سجل' },
  { key: 'storageMb', label: 'مساحة التخزين', unit: 'ميجابايت' },
  { key: 'campaignsPerMonth', label: 'الحملات الشهرية', unit: 'حملة' },
  { key: 'whatsappMessages', label: 'رسائل واتساب الشهرية', unit: 'رسالة' },
] as const;

export const LIMIT_DEF_MAP: Readonly<Record<LimitKey, LimitDef>> =
  Object.fromEntries(LIMIT_DEFS.map((l) => [l.key, l])) as Record<LimitKey, LimitDef>;

export type PlanLimits = Record<LimitKey, number>;

/** هل تجاوز الاستخدام الحد المسموح؟ القيمة -1 تعني بلا حد. */
export function exceedsLimit(limit: number, usage: number): boolean {
  return limit !== UNLIMITED && usage >= limit;
}

/** النسبة المئوية للاستهلاك، أو null إذا كان الحد مفتوحًا. */
export function limitUsageRatio(limit: number, usage: number): number | null {
  if (limit === UNLIMITED) return null;
  if (limit <= 0) return 1;
  return Math.min(usage / limit, 1);
}

/* ==========================================================================
   دورات الفوترة
   ========================================================================== */

/** بحروف كبيرة لتطابق نوع BillingCycle في قاعدة البيانات. */
export const BILLING_CYCLES = ['MONTHLY', 'YEARLY'] as const;
export type BillingCycle = (typeof BILLING_CYCLES)[number];

export interface BillingCycleDef {
  key: BillingCycle;
  label: string;
  months: number;
  /** نسبة الخصم مقارنة بالدفع الشهري. */
  discount: number;
}

export const BILLING_CYCLE_DEFS: readonly BillingCycleDef[] = [
  { key: 'MONTHLY', label: 'شهري', months: 1, discount: 0 },
  { key: 'YEARLY', label: 'سنوي', months: 12, discount: 0.1667 },
] as const;

export const BILLING_CYCLE_MAP: Readonly<Record<BillingCycle, BillingCycleDef>> =
  Object.fromEntries(BILLING_CYCLE_DEFS.map((c) => [c.key, c])) as Record<
    BillingCycle,
    BillingCycleDef
  >;

/* ==========================================================================
   الباقات الافتراضية
   يمكن للوحة المنصة تعديلها أو إضافة باقات مخصّصة لكل مستأجر.
   ========================================================================== */

export interface PlanDef {
  id: string;
  name: string;
  order: number;
  description: string;
  /** السعر بالجنيه المصري لدورة الفوترة المحددة. */
  price: number;
  cycle: BillingCycle;
  isPublic: boolean;
  badge: string;
  features: readonly FeatureKey[];
  limits: PlanLimits;
}

export const DEFAULT_PLANS: readonly PlanDef[] = [
  {
    id: 'plan_starter',
    name: 'الأساسية',
    order: 1,
    description: 'للفرق الصغيرة التي تبدأ بتنظيم عملائها ومبيعاتها.',
    price: 1500,
    cycle: 'MONTHLY',
    isPublic: true,
    badge: '',
    features: ['crm', 'sales'],
    limits: {
      users: 3,
      accounts: 500,
      leads: 2000,
      storageMb: 1024,
      campaignsPerMonth: 0,
      whatsappMessages: 0,
    },
  },
  {
    id: 'plan_pro',
    name: 'الاحترافية',
    order: 2,
    description: 'للشركات النامية التي تحتاج الدورة الكاملة من العميل حتى التحصيل.',
    price: 3500,
    cycle: 'MONTHLY',
    isPublic: true,
    badge: 'الأكثر اختيارًا',
    features: ['crm', 'sales', 'finance', 'hr', 'reports'],
    limits: {
      users: 15,
      accounts: 5000,
      leads: 25000,
      storageMb: 10240,
      campaignsPerMonth: 10,
      whatsappMessages: 5000,
    },
  },
  {
    id: 'plan_enterprise',
    name: 'المؤسسات',
    order: 3,
    description: 'وصول كامل لكل الوحدات بلا حدود على الاستخدام.',
    price: 7500,
    cycle: 'MONTHLY',
    isPublic: true,
    badge: '',
    features: [...FEATURE_KEYS],
    limits: {
      users: UNLIMITED,
      accounts: UNLIMITED,
      leads: UNLIMITED,
      storageMb: UNLIMITED,
      campaignsPerMonth: UNLIMITED,
      whatsappMessages: UNLIMITED,
    },
  },
] as const;
